/**
 * Schema.org Structured Data Generators
 */

import seoConfig from '../config/seoConfig';

const { siteUrl, siteName } = seoConfig;
const logoUrl = `${siteUrl}/logo.png`;

export const generateOrganizationSchema = (options = {}) => {
  const {
    name = siteName,
    url = siteUrl,
    logo = logoUrl,
    sameAs = [],
  } = options;
  const schema = { '@context': 'https://schema.org', '@type': 'Organization', name, url, logo };
  if (sameAs.length > 0) schema.sameAs = sameAs;
  return schema;
};

export const generateWebSiteSchema = (options = {}) => {
  const { name = siteName, url = siteUrl, searchUrl = `${siteUrl}/search` } = options;
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name,
    url,
    potentialAction: {
      '@type': 'SearchAction',
      target: { '@type': 'EntryPoint', urlTemplate: `${searchUrl}?q={search_term_string}` },
      'query-input': 'required name=search_term_string',
    },
  };
};

export const generateOfferSchema = (coupon, options = {}) => {
  if (!coupon) return null;
  const baseUrl = options.baseUrl || siteUrl;
  const couponId = coupon.id || coupon.publicId || coupon._id;
  const description =
    typeof coupon.description === 'string'
      ? coupon.description.replace(/<[^>]+>/g, '').trim()
      : '';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Offer',
    name: coupon.title || coupon.code,
    description: description || undefined,
    url: `${baseUrl}/coupon/${couponId}`,
    price: coupon.salePrice || coupon.discountValue || '0',
    priceCurrency: coupon.currency || 'USD',
    availability: 'https://schema.org/InStock',
  };

  const expires = coupon.expiresAt || coupon.expiryDate;
  if (expires) schema.validThrough = new Date(expires).toISOString();

  if (coupon.storeName) {
    schema.seller = { '@type': 'Organization', name: coupon.storeName };
  }

  if (coupon.productImage || coupon.imageUrl) {
    schema.image = coupon.productImage || coupon.imageUrl;
  }

  return schema;
};

export const generateProductSchema = (coupon) => {
  if (!coupon) return null;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: coupon.title || coupon.code,
    description:
      typeof coupon.description === 'string'
        ? coupon.description.replace(/<[^>]+>/g, '').trim()
        : '',
    image: coupon.productImage || coupon.imageUrl || '',
  };
  if (coupon.discountValue || coupon.discountPercentage) {
    schema.offers = {
      '@type': 'Offer',
      price: coupon.discountValue || '0',
      priceCurrency: coupon.currency || 'USD',
      availability: 'https://schema.org/InStock',
    };
    const expires = coupon.expiresAt || coupon.expiryDate;
    if (expires) schema.offers.validThrough = new Date(expires).toISOString();
  }
  return schema;
};

export const generateStoreSchema = (store) => {
  if (!store) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'Store',
    name: store.name,
    description: store.description || '',
    image: store.logo || '',
    url: store.websiteUrl || store.website || '',
  };
};

export const generateBreadcrumbSchema = (items) => {
  if (!items || items.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
};

export const generateFAQSchema = (faqs) => {
  if (!faqs || faqs.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question || faq.title,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer || faq.content },
    })),
  };
};

export const generateArticleSchema = (article) => {
  if (!article) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt || article.description,
    image: article.featuredImage || article.image,
    datePublished: article.createdAt
      ? new Date(article.createdAt).toISOString()
      : new Date().toISOString(),
    dateModified: article.updatedAt
      ? new Date(article.updatedAt).toISOString()
      : new Date().toISOString(),
    author: { '@type': 'Person', name: article.author?.name || siteName },
    publisher: {
      '@type': 'Organization',
      name: siteName,
      logo: { '@type': 'ImageObject', url: logoUrl },
    },
  };
};

export const generateWebPageSchema = (options = {}) => {
  const { name, description, url } = options;
  if (!name || !url) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name,
    description: description || undefined,
    url,
    isPartOf: { '@type': 'WebSite', name: siteName, url: siteUrl },
  };
};

export const generateCollectionPageSchema = (options = {}) => {
  const { name, description, url, numberOfItems, items = [] } = options;
  const schema = { '@context': 'https://schema.org', '@type': 'CollectionPage', name, description, url };
  if (numberOfItems !== undefined) schema.numberOfItems = numberOfItems;
  if (items.length > 0) {
    schema.mainEntity = {
      '@type': 'ItemList',
      numberOfItems: items.length,
      itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: { '@type': item.type || 'Product', name: item.name, url: item.url },
      })),
    };
  }
  return schema;
};
